import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Siren, ArrowClockwise, Gear, MagnifyingGlass, ArrowRight,
  Headset, Gift, User as UserIcon, Warning,
  CreditCard, DeviceMobile, Lightning, DotsThreeCircle,
  Buildings, Users, Storefront, ListBullets,
  CloudArrowUp, Code, Copy, CheckCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

// ─── Category metadata ───────────────────────────────────────────
const CATEGORIES = [
  { key: 'all', label: 'All', color: 'text-gray-800 border-gray-800', icon: Siren },
  { key: 'lpg_booking', label: 'LPG Booking', color: 'text-indigo-700 border-indigo-700', icon: Headset },
  { key: 'gift_card', label: 'Gift Card', color: 'text-fuchsia-700 border-fuchsia-700', icon: Gift },
  { key: 'card', label: 'Card', color: 'text-purple-700 border-purple-700', icon: CreditCard },
  { key: 'recharge', label: 'Recharge', color: 'text-teal-700 border-teal-700', icon: DeviceMobile },
  { key: 'electricity', label: 'Electricity', color: 'text-yellow-700 border-yellow-700', icon: Lightning },
  { key: 'consumer', label: 'Consumer', color: 'text-amber-700 border-amber-700', icon: UserIcon },
  { key: 'others', label: 'Others', color: 'text-gray-600 border-gray-600', icon: DotsThreeCircle },
];

const STATUS_COLORS = {
  open: 'bg-amber-100 text-amber-800',
  in_progress: 'bg-blue-100 text-blue-800',
  assigned: 'bg-blue-100 text-blue-800',
  forwarded: 'bg-blue-100 text-blue-800',
  under_review: 'bg-orange-100 text-orange-800',
  resolved: 'bg-emerald-100 text-emerald-800',
  closed: 'bg-gray-100 text-gray-700',
  reopened: 'bg-red-100 text-red-800',
  rejected: 'bg-gray-200 text-gray-700',
  auto_refund_pending: 'bg-red-100 text-red-800',
  refund_approved: 'bg-emerald-100 text-emerald-800',
  refund_rejected: 'bg-gray-100 text-gray-700',
};

const CATEGORY_BADGE = {
  lpg_booking: 'bg-indigo-100 text-indigo-800',
  gift_card: 'bg-fuchsia-100 text-fuchsia-800',
  card: 'bg-purple-100 text-purple-800',
  recharge: 'bg-teal-100 text-teal-800',
  electricity: 'bg-yellow-100 text-yellow-800',
  consumer: 'bg-amber-100 text-amber-800',
  others: 'bg-gray-100 text-gray-700',
};

const ROLE_LABELS = {
  owner: 'Owner', admin: 'Admin', assistant: 'Assistant', accountant: 'Accountant (CA)',
  staff: 'Staff (Team Lead)', sub_staff: 'Sub-Staff',
  distributor: 'Distributor', sales_person: 'Sales Person',
  gift_card_vendor: 'Gift Card Vendor', card_vendor: 'Card Vendor',
  recharge_vendor: 'Recharge Vendor', electricity_vendor: 'Electricity Vendor',
};

function fmtDate(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return s; }
}
function fmt(n) {
  const v = Number(n || 0);
  if (!v) return '—';
  return '₹' + v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function ComplaintsHubPage() {
  const { user } = useAuth();
  const isAdmin = ['owner', 'admin', 'assistant'].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ by_category: {}, by_status: {}, open: 0, resolved: 0 });
  const [category, setCategory] = useState('all');
  const [status, setStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAccess, setShowAccess] = useState(false);
  const [accessCfg, setAccessCfg] = useState({ roles: [], all_roles: [], editable: false });
  const [savingAccess, setSavingAccess] = useState(false);
  // Group-by state
  const [groupMode, setGroupMode] = useState('flat'); // flat | agency | assignee | vendor | raiser
  const [drillGroup, setDrillGroup] = useState(null); // { key, label, rows } — opens drill-down modal

  // Sheet Sync state
  const [showSync, setShowSync] = useState(false);
  const [syncCfg, setSyncCfg] = useState({
    sheet_url: '', webhook_url: '',
    last_sync_at: null, last_sync_count: 0, last_test_at: null,
    updated_at: null, updated_by: '',
  });
  const [syncEditSheet, setSyncEditSheet] = useState('');
  const [syncEditWebhook, setSyncEditWebhook] = useState('');
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [showAppsScript, setShowAppsScript] = useState(false);
  const [appsScriptCode, setAppsScriptCode] = useState('');
  const [appsScriptHeaders, setAppsScriptHeaders] = useState([]);
  const [appsScriptCopied, setAppsScriptCopied] = useState(false);
  const [headerRowCopied, setHeaderRowCopied] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category && category !== 'all') params.set('category', category);
      if (status) params.set('status', status);
      if (search) params.set('q', search);
      const [listR, statR] = await Promise.all([
        api.get(`/complaints-hub?${params.toString()}`),
        api.get('/complaints-hub/stats'),
      ]);
      setRows(listR.data || []);
      setStats(statR.data || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Load failed');
      setRows([]);
    } finally { setLoading(false); }
  }, [category, status, search]);

  useEffect(() => { load(); }, [load]);

  const openAccess = async () => {
    try {
      const r = await api.get('/complaints-hub/access-config');
      setAccessCfg(r.data || { roles: [], all_roles: [], editable: false });
      setShowAccess(true);
    } catch (err) { toast.error('Failed to load config'); }
  };

  const toggleRole = (role) => {
    if (role === 'owner') return; // owner always
    setAccessCfg((p) => {
      const has = p.roles.includes(role);
      return { ...p, roles: has ? p.roles.filter((r) => r !== role) : [...p.roles, role] };
    });
  };

  const saveAccess = async () => {
    setSavingAccess(true);
    try {
      await api.put('/complaints-hub/access-config', { roles: accessCfg.roles });
      toast.success('Access config saved');
      setShowAccess(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSavingAccess(false); }
  };

  // ─── Sheet Sync handlers ────────────────────────────────────
  const openSyncSettings = async () => {
    setShowSync(true);
    setSyncLoading(true);
    try {
      const r = await api.get('/complaints-hub/sync-settings');
      const cfg = r.data || {};
      setSyncCfg(cfg);
      setSyncEditSheet(cfg.sheet_url || '');
      setSyncEditWebhook(cfg.webhook_url || '');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sync settings load fail');
    } finally { setSyncLoading(false); }
  };

  const saveSyncSettings = async () => {
    setSyncBusy(true);
    try {
      await api.post('/complaints-hub/sync-settings', {
        sheet_url: syncEditSheet.trim(),
        webhook_url: syncEditWebhook.trim(),
      });
      toast.success('Sync settings saved');
      // Refresh
      const r = await api.get('/complaints-hub/sync-settings');
      setSyncCfg(r.data || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save fail');
    } finally { setSyncBusy(false); }
  };

  const testSyncWebhook = async () => {
    setSyncBusy(true);
    try {
      const r = await api.post('/complaints-hub/sync-test-webhook', {});
      if (r.data?.ok) toast.success(`Ping OK · Sheet ne ${r.data.status} bheja`);
      else toast.error(`Ping ne ${r.data?.status} return kiya — Apps Script check karo`);
      const g = await api.get('/complaints-hub/sync-settings');
      setSyncCfg(g.data || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Webhook reach fail');
    } finally { setSyncBusy(false); }
  };

  const runSyncNow = async () => {
    setSyncBusy(true);
    try {
      const r = await api.post('/complaints-hub/sync-now', {});
      toast.success(`Sync done · ${r.data?.rows_sent || 0} rows push kiye`);
      const g = await api.get('/complaints-hub/sync-settings');
      setSyncCfg(g.data || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sync fail');
    } finally { setSyncBusy(false); }
  };

  const openAppsScript = async () => {
    setShowAppsScript(true);
    setAppsScriptCopied(false);
    setHeaderRowCopied(false);
    if (!appsScriptCode) {
      try {
        const r = await api.get('/complaints-hub/apps-script');
        setAppsScriptCode(r.data?.code || '');
        setAppsScriptHeaders(r.data?.header_row || []);
      } catch (err) {
        toast.error('Apps Script load fail');
      }
    }
  };

  const clipboardCopy = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      }
      return true;
    } catch { return false; }
  };

  const copyAppsScript = async () => {
    const ok = await clipboardCopy(appsScriptCode);
    if (ok) {
      setAppsScriptCopied(true);
      toast.success('Apps Script code copied');
      setTimeout(() => setAppsScriptCopied(false), 2500);
    } else toast.error('Copy fail');
  };

  const copyHeaderRow = async () => {
    const ok = await clipboardCopy(appsScriptHeaders.join('\t'));
    if (ok) {
      setHeaderRowCopied(true);
      toast.success('Header row copied — sheet ke row 1 me Ctrl+V');
      setTimeout(() => setHeaderRowCopied(false), 2500);
    } else toast.error('Copy fail');
  };

  const categoryCounts = useMemo(() => ({
    all: stats.by_category?.total || 0,
    lpg_booking: stats.by_category?.lpg_booking || 0,
    gift_card: stats.by_category?.gift_card || 0,
    card: stats.by_category?.card || 0,
    recharge: stats.by_category?.recharge || 0,
    electricity: stats.by_category?.electricity || 0,
    consumer: stats.by_category?.consumer || 0,
    others: stats.by_category?.others || 0,
  }), [stats]);

  // ── Group-by computation ────────────────────────────────
  const GROUP_MODES = [
    { key: 'flat',     label: 'List',         icon: ListBullets,  color: 'text-gray-700' },
    { key: 'agency',   label: 'By Agency',    icon: Buildings,    color: 'text-indigo-700' },
    { key: 'assignee', label: 'By Team Member', icon: Users,      color: 'text-emerald-700' },
    { key: 'vendor',   label: 'By Vendor',    icon: Storefront,   color: 'text-fuchsia-700' },
    { key: 'raiser',   label: 'By Raiser',    icon: UserIcon,     color: 'text-amber-700' },
  ];

  const groupKeyFor = (r, mode) => {
    if (mode === 'agency')   return r.agency_name || null;
    if (mode === 'assignee') return r.assigned_to_name && r.assigned_to_name !== '—' ? r.assigned_to_name : null;
    if (mode === 'vendor')   return r.vendor_name || null;
    if (mode === 'raiser')   return r.raised_by_name && r.raised_by_name !== '—' ? r.raised_by_name : null;
    return null;
  };

  // Google Sheets-friendly TSV: tab-separated cells, newline-separated rows.
  // Each cell: strip tabs/newlines/carriage-returns to plain text so paste
  // spreads cleanly across columns with zero parsing errors.
  const sheetCell = (v) => {
    if (v == null) return '';
    return String(v)
      .replace(/\r?\n/g, ' ⏎ ')  // preserve multi-line intent visually
      .replace(/\t/g, ' ')        // tabs would break TSV column alignment
      .replace(/"/g, "'")         // stray quotes → apostrophe
      .trim();
  };

  const copyGroupToSheet = async (grp) => {
    if (!grp || !grp.rows?.length) return;
    const headers = [
      'Ref#', 'Category', 'Status', 'Subject', 'Description',
      'Raised By', 'Role', 'Assigned To', 'Amount',
      'Agency', 'Vendor', 'Consumer ID', 'Created At',
    ];
    const lines = [headers.join('\t')];
    for (const r of grp.rows) {
      lines.push([
        sheetCell(r.ref_no),
        sheetCell(r.category_label),
        sheetCell(String(r.status || '').toUpperCase()),
        sheetCell(r.subject),
        sheetCell(r.description),
        sheetCell(r.raised_by_name),
        sheetCell(r.raised_by_role),
        sheetCell(r.assigned_to_name),
        sheetCell(r.amount != null ? r.amount : ''),
        sheetCell(r.agency_name),
        sheetCell(r.vendor_name),
        sheetCell(r.consumer_id),
        sheetCell(fmtDate(r.created_at)),
      ].join('\t'));
    }
    const tsv = lines.join('\n');
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(tsv);
      } else {
        // Fallback for insecure contexts / older browsers
        const ta = document.createElement('textarea');
        ta.value = tsv;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      }
      toast.success(`${grp.rows.length} rows copied — Google Sheet me Ctrl+V karo`);
    } catch (err) {
      toast.error('Copy fail — text manually select karke Ctrl+C try karo');
    }
  };

  const groups = useMemo(() => {
    if (groupMode === 'flat') return [];
    const byKey = new Map();
    let unassigned = 0;
    for (const r of rows) {
      const key = groupKeyFor(r, groupMode);
      if (!key) { unassigned += 1; continue; }
      if (!byKey.has(key)) {
        byKey.set(key, {
          key, label: key, rows: [], total: 0,
          by_category: {}, by_status: {},
          latest: null,
          role: r.raised_by_role,
        });
      }
      const g = byKey.get(key);
      g.rows.push(r);
      g.total += 1;
      g.by_category[r.category] = (g.by_category[r.category] || 0) + 1;
      g.by_status[r.status] = (g.by_status[r.status] || 0) + 1;
      if (!g.latest || (r.created_at && r.created_at > g.latest)) g.latest = r.created_at;
    }
    const arr = Array.from(byKey.values()).sort((a, b) => b.total - a.total);
    if (unassigned > 0) {
      arr.push({
        key: '__unassigned__', label: '(Unassigned / N/A)', rows: rows.filter(r => !groupKeyFor(r, groupMode)),
        total: unassigned, by_category: {}, by_status: {}, latest: null,
      });
    }
    return arr;
  }, [rows, groupMode]);

  return (
    <div className="space-y-4" data-testid="complaints-hub-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Siren size={26} weight="duotone" className="text-red-600" /> Complaints Hub
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Saari complaints ek jagah — LPG Support Tickets, Voucher Complaints, aur Consumer Complaints.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="hub-refresh-btn">
            <ArrowClockwise size={14} className="mr-1" /> Refresh
          </Button>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={openSyncSettings} data-testid="hub-sheet-sync-btn">
              <CloudArrowUp size={14} className="mr-1" /> Sheet Sync
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={openAccess} data-testid="hub-access-btn">
            <Gear size={14} className="mr-1" /> Access Control
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        <SummaryCard color="gray" label="Total" value={stats.by_category?.total || 0} />
        <SummaryCard color="indigo" label="LPG Booking" value={stats.by_category?.lpg_booking || 0} />
        <SummaryCard color="fuchsia" label="Gift Card" value={stats.by_category?.gift_card || 0} />
        <SummaryCard color="purple" label="Card" value={stats.by_category?.card || 0} />
        <SummaryCard color="teal" label="Recharge" value={stats.by_category?.recharge || 0} />
        <SummaryCard color="yellow" label="Electricity" value={stats.by_category?.electricity || 0} />
        <SummaryCard color="amber" label="Consumer" value={stats.by_category?.consumer || 0} />
        <SummaryCard color="red" label="Open" value={stats.open || 0} />
      </div>

      {/* Category Tabs (Gift-Card-Vendors style) */}
      <div className="border-b border-gray-200 flex flex-wrap items-center gap-1 overflow-x-auto">
        {CATEGORIES.map((c) => {
          const active = category === c.key;
          const Icon = c.icon;
          return (
            <button
              key={c.key}
              onClick={() => setCategory(c.key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition ${
                active ? `${c.color} bg-white` : 'text-gray-500 border-transparent hover:text-gray-800'
              }`}
              data-testid={`cat-tab-${c.key}`}
            >
              <Icon size={16} weight={active ? 'fill' : 'regular'} />
              {c.label} <span className="text-xs opacity-70">({categoryCounts[c.key] ?? 0})</span>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search ref#, description, agency, vendor, consumer id..."
            className="pl-8 h-9"
            data-testid="hub-search"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-9 px-3 border rounded text-sm bg-white"
          data-testid="hub-status-filter"
        >
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="assigned">Assigned</option>
          <option value="forwarded">Forwarded</option>
          <option value="under_review">Under Review</option>
          <option value="reopened">Reopened</option>
          <option value="auto_refund_pending">Refund Pending</option>
          <option value="resolved">Resolved</option>
          <option value="refund_approved">Refund Approved</option>
          <option value="refund_rejected">Refund Rejected</option>
          <option value="rejected">Rejected</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {/* Group Mode Toggle */}
      <div className="bg-white border rounded-xl p-2 flex items-center gap-2 flex-wrap" data-testid="hub-group-toggle">
        <span className="text-[10px] uppercase text-gray-500 font-semibold tracking-widest px-2">View:</span>
        {GROUP_MODES.map((m) => {
          const Ic = m.icon;
          const active = groupMode === m.key;
          return (
            <button
              key={m.key}
              onClick={() => setGroupMode(m.key)}
              data-testid={`hub-group-${m.key}`}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full font-medium transition ${
                active ? 'bg-gray-900 text-white shadow' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Ic size={14} /> {m.label}
              {active && groupMode !== 'flat' && (
                <span className="bg-white/25 text-white px-1.5 py-0 rounded text-[9px] ml-1">{groups.length}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Table (flat) */}
      {groupMode === 'flat' && (
      <div className="bg-white border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500" data-testid="hub-empty">
            Koi complaint nahi. Category / status filter change karke try karein.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Category</th>
                  <th className="px-3 py-2 text-left">Ref #</th>
                  <th className="px-3 py-2 text-left">Subject / Type</th>
                  <th className="px-3 py-2 text-left">Raised By</th>
                  <th className="px-3 py-2 text-left">Assigned To</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Created</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.hub_id} className="border-t hover:bg-gray-50" data-testid={`hub-row-${r.hub_id}`}>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${CATEGORY_BADGE[r.category] || 'bg-gray-100 text-gray-700'}`}>
                        {r.category_label}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs font-bold text-indigo-700">{r.ref_no}</td>
                    <td className="px-3 py-2 text-xs">
                      <div className="font-semibold">{r.subject}</div>
                      <div className="text-gray-500 text-[11px] line-clamp-1 max-w-[320px]">{r.description || '—'}</div>
                      {(r.agency_name || r.vendor_name || r.consumer_id) && (
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          {r.agency_name && <>Agency: {r.agency_name} · </>}
                          {r.vendor_name && <>Vendor: {r.vendor_name} · </>}
                          {r.consumer_id && <>Consumer: {r.consumer_id}</>}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className="font-medium">{r.raised_by_name}</div>
                      <div className="text-[10px] text-gray-500">{r.raised_by_role}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">{r.assigned_to_name || '—'}</td>
                    <td className="px-3 py-2 text-right text-xs font-semibold">{fmt(r.amount)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${STATUS_COLORS[r.status] || 'bg-gray-100 text-gray-700'}`}>
                        {String(r.status || 'open').replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-[11px] text-gray-500">{fmtDate(r.created_at)}</td>
                    <td className="px-3 py-2 text-right">
                      <Link to={r.detail_link || '#'} className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline" data-testid={`hub-open-${r.hub_id}`}>
                        Open <ArrowRight size={12} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* Grouped Cards View (agency / assignee / vendor / raiser) */}
      {groupMode !== 'flat' && (
        <div className="bg-white border rounded-xl p-3" data-testid="hub-grouped-view">
          {loading ? (
            <p className="text-center text-sm text-gray-500 p-8">Loading…</p>
          ) : groups.length === 0 ? (
            <p className="text-center text-sm text-gray-500 p-8">
              Is view me koi grouping key nahi mili. Doosra view try karein.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="hub-group-cards">
              {groups.map((g) => {
                const isUnass = g.key === '__unassigned__';
                const openCount = ['open', 'in_progress', 'assigned', 'forwarded', 'under_review', 'reopened', 'auto_refund_pending']
                  .reduce((sum, k) => sum + (g.by_status[k] || 0), 0);
                return (
                  <button
                    key={g.key}
                    onClick={() => setDrillGroup(g)}
                    data-testid={`hub-group-card-${g.key}`}
                    className={`bg-white border-2 rounded-xl p-3 text-left hover:shadow-lg hover:-translate-y-0.5 transition-all ${
                      isUnass ? 'border-gray-200 opacity-70' : 'border-gray-200 hover:border-indigo-400'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-gray-900 truncate text-sm" title={g.label}>{g.label}</p>
                        {g.role && !isUnass && (
                          <p className="text-[10px] text-gray-500 uppercase tracking-wide">{ROLE_LABELS[g.role] || g.role}</p>
                        )}
                      </div>
                      <span className="bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">{g.total} total</span>
                    </div>
                    {/* Category breakdown chips */}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {Object.entries(g.by_category).map(([cat, count]) => (
                        <span key={cat} className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${CATEGORY_BADGE[cat] || 'bg-gray-100 text-gray-700'}`}>
                          {(CATEGORIES.find(c => c.key === cat)?.label || cat).slice(0, 12)}: {count}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between text-[10px]">
                      <span className={openCount > 0 ? 'text-red-700 font-semibold' : 'text-emerald-700 font-semibold'}>
                        {openCount > 0 ? `⚠ ${openCount} open` : '✓ all resolved'}
                      </span>
                      <span className="text-gray-400">{fmtDate(g.latest)}</span>
                    </div>
                    <div className="mt-2 text-[10px] text-indigo-600 font-semibold flex items-center gap-1">
                      View all {g.total} complaints <ArrowRight size={11} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Drill-Down Modal — all complaints for one group */}
      <Dialog open={!!drillGroup} onOpenChange={(o) => !o && setDrillGroup(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0" data-testid="hub-drill-modal">
          {drillGroup && (
            <>
              <div className="p-4 border-b bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-white/80">
                      {groupMode === 'agency' ? '🏢 Agency' :
                       groupMode === 'assignee' ? '👤 Team Member' :
                       groupMode === 'vendor' ? '🎪 Vendor' :
                       '🧑 Raiser'}
                    </p>
                    <h3 className="text-xl font-bold" data-testid="hub-drill-title">{drillGroup.label}</h3>
                    <p className="text-xs text-white/90 mt-0.5">{drillGroup.total} complaints · {Object.keys(drillGroup.by_category).length} categories</p>
                  </div>
                </div>
              </div>

              {/* Summary tiles */}
              <div className="p-3 border-b grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(drillGroup.by_status).map(([st, count]) => (
                  <div key={st} className="bg-slate-50 border border-slate-200 rounded p-2 text-center">
                    <p className={`text-[10px] uppercase font-semibold ${STATUS_COLORS[st] || 'text-gray-600'}`}>{st.replace('_',' ')}</p>
                    <p className="text-lg font-bold text-gray-900">{count}</p>
                  </div>
                ))}
              </div>

              {/* Full complaint list */}
              <div className="flex-1 overflow-y-auto p-3" data-testid="hub-drill-list">
                <div className="space-y-2">
                  {drillGroup.rows.map((r) => (
                    <div key={r.hub_id} className="border rounded-lg p-3 hover:bg-slate-50" data-testid={`hub-drill-row-${r.hub_id}`}>
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${CATEGORY_BADGE[r.category] || 'bg-gray-100 text-gray-700'}`}>
                            {r.category_label}
                          </span>
                          <span className="font-mono text-xs font-bold text-indigo-700">{r.ref_no}</span>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${STATUS_COLORS[r.status] || 'bg-gray-100 text-gray-700'}`}>
                            {String(r.status || 'open').replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        <Link to={r.detail_link || '#'} className="text-xs text-indigo-600 hover:underline font-medium">Open →</Link>
                      </div>
                      <p className="font-semibold text-sm text-gray-900 mt-1.5">{r.subject}</p>
                      {r.description && <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">{r.description}</p>}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2 text-[11px]">
                        <div>
                          <p className="text-gray-500">Raised By</p>
                          <p className="font-medium">{r.raised_by_name} <span className="text-gray-400">({r.raised_by_role})</span></p>
                        </div>
                        <div>
                          <p className="text-gray-500">Assigned To</p>
                          <p className="font-medium">{r.assigned_to_name || '—'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Amount</p>
                          <p className="font-semibold text-emerald-700">{fmt(r.amount)}</p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center mt-2 text-[10px] text-gray-500">
                        <span>{fmtDate(r.created_at)}</span>
                        <span>{r.agency_name || r.vendor_name || r.consumer_id || ''}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-3 border-t bg-slate-50 flex justify-between gap-2 flex-wrap">
                <button
                  data-testid="hub-drill-copy-btn"
                  onClick={() => copyGroupToSheet(drillGroup)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded flex items-center gap-1.5"
                >
                  📋 Copy for Google Sheet ({drillGroup.rows.length} rows)
                </button>
                <Button variant="outline" onClick={() => setDrillGroup(null)} data-testid="hub-drill-close">Close</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Sheet Sync Settings Dialog */}
      <Dialog open={showSync} onOpenChange={setShowSync}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sync-settings-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CloudArrowUp size={18} weight="duotone" className="text-emerald-600" />
              Google Sheet Auto-Sync
            </DialogTitle>
            <DialogDescription className="text-xs">
              Complaints Hub ki saari complaints aapki apni Google Sheet me webhook ke through push karo.
            </DialogDescription>
          </DialogHeader>
          {syncLoading ? (
            <div className="p-6 text-center text-sm text-gray-500">Loading…</div>
          ) : (
            <div className="space-y-4">
              <div className="bg-indigo-50 border border-indigo-200 rounded p-3 text-xs text-indigo-900">
                <b>Kaise kaam karta hai:</b> Complaints Hub ki saari complaints
                automatically aapki apni Google Sheet me daily push hoti hain.
                Multi-card complaints ko line-by-line rows me expand kiya jaata
                hai (dedup by Ref# + Card #). Setup 5 minute me:
                <ol className="list-decimal ml-4 mt-1.5 space-y-0.5">
                  <li>Blank Google Sheet banao</li>
                  <li>Neeche &quot;Apps Script Code Dekho&quot; se code copy karke Apps Script me paste karo</li>
                  <li>Web app ke roop me deploy karo (Access: Anyone)</li>
                  <li>Us /exec URL ko neeche &quot;Webhook URL&quot; me daalo</li>
                  <li>&quot;Test Ping&quot; karo, phir &quot;Sync Now&quot; chalao</li>
                </ol>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700">Sheet URL <span className="text-gray-400 font-normal">(optional — sirf reference ke liye)</span></label>
                <Input
                  value={syncEditSheet}
                  onChange={(e) => setSyncEditSheet(e.target.value)}
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  className="mt-1 text-sm"
                  data-testid="sync-sheet-url-input"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-700">Webhook URL <span className="text-red-600">*</span></label>
                <Input
                  value={syncEditWebhook}
                  onChange={(e) => setSyncEditWebhook(e.target.value)}
                  placeholder="https://script.google.com/macros/s/AKfyc.../exec"
                  className="mt-1 text-sm font-mono"
                  data-testid="sync-webhook-url-input"
                />
                <p className="text-[10px] text-gray-500 mt-1">Apps Script Deploy → Web app ke baad milne wala /exec URL.</p>
              </div>

              {/* Status card */}
              <div className="bg-slate-50 border rounded p-3 space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Sync:</span>
                  <span className="font-mono">
                    {syncCfg.last_sync_at
                      ? `${fmtDate(syncCfg.last_sync_at)} · ${syncCfg.last_sync_count || 0} rows`
                      : '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Test Ping:</span>
                  <span className="font-mono">{syncCfg.last_test_at ? fmtDate(syncCfg.last_test_at) : '—'}</span>
                </div>
                {syncCfg.updated_by && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Last Updated By:</span>
                    <span>{syncCfg.updated_by}</span>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Button
                  onClick={saveSyncSettings}
                  disabled={syncBusy}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="sync-save-btn"
                >
                  {syncBusy ? 'Saving…' : 'Save'}
                </Button>
                <Button
                  onClick={testSyncWebhook}
                  disabled={syncBusy || !syncCfg.webhook_url}
                  variant="outline"
                  data-testid="sync-test-btn"
                >
                  Test Ping
                </Button>
                <Button
                  onClick={runSyncNow}
                  disabled={syncBusy || !syncCfg.webhook_url}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="sync-now-btn"
                >
                  Sync Now
                </Button>
                <Button
                  onClick={openAppsScript}
                  variant="outline"
                  data-testid="sync-view-script-btn"
                >
                  <Code size={14} className="mr-1" /> Apps Script
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSync(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apps Script Code Viewer Dialog */}
      <Dialog open={showAppsScript} onOpenChange={setShowAppsScript}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="apps-script-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code size={18} weight="duotone" className="text-purple-600" />
              Google Apps Script — Paste in your Sheet
            </DialogTitle>
            <DialogDescription className="text-xs">
              Header row + full Apps Script code copy karke apne Google Sheet me deploy karo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Step 1: Header row */}
            <div className="bg-amber-50 border border-amber-200 rounded p-3">
              <p className="text-xs font-bold text-amber-900 mb-1.5">Step 1: Sheet ke row 1 me ye headers paste karo</p>
              <div className="flex gap-2 items-start">
                <code className="flex-1 text-[10px] bg-white border rounded p-2 font-mono overflow-x-auto whitespace-nowrap">
                  {appsScriptHeaders.join('  ·  ')}
                </code>
                <Button
                  size="sm"
                  onClick={copyHeaderRow}
                  className={headerRowCopied ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-600 hover:bg-amber-700'}
                  data-testid="copy-header-row-btn"
                >
                  {headerRowCopied ? <><CheckCircle size={13} className="mr-1" /> Copied</> : <><Copy size={13} className="mr-1" /> Copy</>}
                </Button>
              </div>
            </div>

            {/* Step 2: Apps Script Code */}
            <div className="bg-slate-50 border border-slate-200 rounded p-3">
              <div className="flex justify-between items-start mb-1.5">
                <p className="text-xs font-bold text-slate-900">
                  Step 2: Extensions → Apps Script → ye code paste karo → Save → Deploy → Web app
                </p>
                <Button
                  size="sm"
                  onClick={copyAppsScript}
                  className={appsScriptCopied ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-800 hover:bg-slate-900'}
                  data-testid="copy-apps-script-btn"
                >
                  {appsScriptCopied ? <><CheckCircle size={13} className="mr-1" /> Copied</> : <><Copy size={13} className="mr-1" /> Copy Code</>}
                </Button>
              </div>
              <pre className="text-[10px] bg-gray-900 text-emerald-300 rounded p-3 overflow-x-auto max-h-96 font-mono leading-snug" data-testid="apps-script-code-block">
                {appsScriptCode || 'Loading…'}
              </pre>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
              <b>Deploy tips:</b>
              <ul className="list-disc ml-4 mt-1 space-y-0.5">
                <li>Deploy → New deployment → Type: <b>Web app</b></li>
                <li>Execute as: <b>Me</b> · Who has access: <b>Anyone</b></li>
                <li>Deploy karke jo /exec URL milegi, wahi Webhook URL me paste karo</li>
                <li>Har naye deployment version pe URL badalti hai — dubara paste kar do</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAppsScript(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Access Control Dialog */}
      <Dialog open={showAccess} onOpenChange={setShowAccess}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gear size={18} /> Complaints Hub — Access Control
            </DialogTitle>
          </DialogHeader>
          {!accessCfg.editable && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex gap-2">
              <Warning size={14} className="mt-0.5" />
              Sirf Owner / Admin edit kar sakte hain. Aap view-only mode me hain.
            </div>
          )}
          <p className="text-xs text-gray-600">
            Select karo kaunse roles Complaints Hub dekh sakte hain. <b>Owner</b> hamesha allowed hai.
          </p>
          <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto">
            {(accessCfg.all_roles || []).map((r) => {
              const checked = accessCfg.roles.includes(r);
              const isOwner = r === 'owner';
              return (
                <label key={r} className={`flex items-center gap-2 p-2 border rounded text-sm cursor-pointer ${
                  checked ? 'bg-indigo-50 border-indigo-300' : 'bg-white border-gray-200'
                } ${isOwner ? 'opacity-60 cursor-not-allowed' : ''}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={isOwner || !accessCfg.editable}
                    onChange={() => toggleRole(r)}
                    data-testid={`access-role-${r}`}
                  />
                  <span className="text-xs font-medium">{ROLE_LABELS[r] || r}</span>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAccess(false)}>Cancel</Button>
            <Button
              onClick={saveAccess}
              disabled={!accessCfg.editable || savingAccess}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-access-btn"
            >
              {savingAccess ? 'Saving…' : 'Save Access Config'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ color, label, value }) {
  const map = {
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    fuchsia: 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    teal: 'bg-teal-50 border-teal-200 text-teal-700',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    red: 'bg-red-50 border-red-200 text-red-700',
  };
  return (
    <div className={`border rounded-lg p-2 ${map[color] || map.gray}`}>
      <div className="text-[9px] uppercase font-bold opacity-80 leading-tight">{label}</div>
      <div className="text-xl font-bold mt-0.5">{value ?? 0}</div>
    </div>
  );
}
